import { useState, useEffect, useCallback, useMemo } from 'react';
import { useReadContract, usePublicClient } from 'wagmi';
import { formatUnits, type Abi, type Log, parseAbiItem } from 'viem';
import { CONTRACTS } from './addresses';
import SymsvoxEscrowAbi from './abis/SymsvoxEscrow.json';

const escrowAbi = SymsvoxEscrowAbi as Abi;

// ---------- Types ----------

export type EscrowStatus = 'ACTIVE' | 'RELEASED' | 'DISPUTED' | 'RECLAIMED';

const STATUS_MAP: Record<number, EscrowStatus> = {
  0: 'ACTIVE',
  1: 'RELEASED',
  2: 'DISPUTED',
  3: 'RECLAIMED',
};

export interface EscrowDetail {
  depositor: string;
  agentId: bigint;
  amount: bigint;
  amountFormatted: string;
  createdAt: bigint;
  deadline: bigint;
  taskHash: string;
  status: EscrowStatus;
  statusRaw: number;
}

export interface EscrowEvent {
  type: 'Created' | 'Released' | 'Disputed' | 'Reclaimed' | 'DisputeResolved';
  blockNumber: bigint;
  transactionHash: string;
  args: Record<string, unknown>;
}

export interface UseEscrowDetailResult {
  escrow: EscrowDetail | null;
  events: EscrowEvent[];
  isLoading: boolean;
  isError: boolean;
  notFound: boolean;
  refetch: () => void;
}

// ---------- Event ABI items for getLogs ----------

const eventAbis = {
  EscrowCreated: parseAbiItem(
    'event EscrowCreated(uint256 indexed escrowId, address indexed depositor, uint256 agentId, uint256 amount, uint256 deadline, bytes32 taskHash)'
  ),
  EscrowReleased: parseAbiItem(
    'event EscrowReleased(uint256 indexed escrowId, address indexed recipient, uint256 payout, uint256 fee)'
  ),
  EscrowDisputed: parseAbiItem(
    'event EscrowDisputed(uint256 indexed escrowId, address indexed depositor)'
  ),
  EscrowReclaimed: parseAbiItem(
    'event EscrowReclaimed(uint256 indexed escrowId, address indexed depositor, uint256 amount)'
  ),
  DisputeResolved: parseAbiItem(
    'event DisputeResolved(uint256 indexed escrowId, bool released)'
  ),
};

// ---------- Hook ----------

export function useEscrowDetail(escrowId: number | null): UseEscrowDetailResult {
  const publicClient = usePublicClient();
  const [events, setEvents] = useState<EscrowEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const enabled = escrowId !== null && escrowId >= 1;

  // Read the escrow struct
  const {
    data: rawEscrow,
    isLoading: readLoading,
    isError,
    refetch: refetchRead,
  } = useReadContract({
    address: CONTRACTS.escrow,
    abi: escrowAbi,
    functionName: 'getEscrow',
    args: enabled ? [BigInt(escrowId)] : undefined,
    query: { enabled },
  });

  // Parse the struct result
  const escrow = useMemo<EscrowDetail | null>(() => {
    if (!rawEscrow || !enabled) return null;

    const result = rawEscrow as readonly [string, bigint, bigint, bigint, bigint, string, number];
    const [depositor, agentId, amount, createdAt, deadline, taskHash, statusRaw] = result;

    // Zero depositor means escrow doesn't exist
    if (depositor === '0x0000000000000000000000000000000000000000') return null;

    return {
      depositor,
      agentId,
      amount,
      amountFormatted: formatUnits(amount, 6),
      createdAt,
      deadline,
      taskHash,
      status: STATUS_MAP[statusRaw] ?? 'ACTIVE',
      statusRaw,
    };
  }, [rawEscrow, enabled]);

  // Fetch event logs for this escrow ID
  const fetchEvents = useCallback(async () => {
    if (!publicClient || !enabled) {
      setEvents([]);
      return;
    }

    setEventsLoading(true);
    try {
      const id = BigInt(escrowId!);
      const address = CONTRACTS.escrow;

      // Fetch all 5 event types in parallel
      const [created, released, disputed, reclaimed, resolved] = await Promise.all([
        publicClient.getLogs({
          address,
          event: eventAbis.EscrowCreated,
          args: { escrowId: id },
          fromBlock: 0n,
          toBlock: 'latest',
        }),
        publicClient.getLogs({
          address,
          event: eventAbis.EscrowReleased,
          args: { escrowId: id },
          fromBlock: 0n,
          toBlock: 'latest',
        }),
        publicClient.getLogs({
          address,
          event: eventAbis.EscrowDisputed,
          args: { escrowId: id },
          fromBlock: 0n,
          toBlock: 'latest',
        }),
        publicClient.getLogs({
          address,
          event: eventAbis.EscrowReclaimed,
          args: { escrowId: id },
          fromBlock: 0n,
          toBlock: 'latest',
        }),
        publicClient.getLogs({
          address,
          event: eventAbis.DisputeResolved,
          args: { escrowId: id },
          fromBlock: 0n,
          toBlock: 'latest',
        }),
      ]);

      const mapLog = (type: EscrowEvent['type']) => (log: Log) => ({
        type,
        blockNumber: log.blockNumber ?? 0n,
        transactionHash: log.transactionHash ?? '',
        args: (log as Log & { args?: Record<string, unknown> }).args ?? {},
      });

      const all: EscrowEvent[] = [
        ...created.map(mapLog('Created')),
        ...released.map(mapLog('Released')),
        ...disputed.map(mapLog('Disputed')),
        ...reclaimed.map(mapLog('Reclaimed')),
        ...resolved.map(mapLog('DisputeResolved')),
      ].sort((a, b) => Number(a.blockNumber - b.blockNumber));

      setEvents(all);
    } catch (err) {
      console.error('[useEscrowDetail] Failed to fetch events:', err);
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [publicClient, enabled, escrowId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const refetch = useCallback(() => {
    refetchRead();
    fetchEvents();
  }, [refetchRead, fetchEvents]);

  const notFound = enabled && !readLoading && !isError && escrow === null;

  return {
    escrow,
    events,
    isLoading: readLoading || eventsLoading,
    isError,
    notFound,
    refetch,
  };
}
